import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { LEVEL } from '../hooks/usePermission';
import { useGlobalStore } from '../stores/globalStore';
import { useFeatureVisibilityStore } from '../stores/featureVisibilityStore';
import { useFacilitySettings } from '../api/selectiveTouchApi';
import { useMealPatternFacilitySettings } from '../hooks/useFacilitySettings';
import { usePurchasingLicensed } from '../api/licensesApi';
import { usePurchasingFeatures } from '../api/purchasing/features';
import { useIsPsiStaff } from '../utils/psiStaff';
import { useAssessmentsOff } from '../pages/clinical/assessmentsOff';
import { useAssessmentOnlyLicence } from '../pages/tables/useTablesMenuFlags';
import { useOptionalPermissionEnforcement } from '../hooks/usePermissionEnforcement';
import { stagedRouteAllows } from '../components/common/stagedRouteRules';
import type { NavSource } from '../components/common/commandPalette';
import {
  buildNavSections,
  locate,
  navSearchSources,
  type NavContext,
  type NavPageView,
  type NavSectionView,
} from './navConfig';
import { useNavEntrySection } from './navEntry';
import { displayEhrTitle } from '../utils/ehrDisplayName';

/**
 * The user, role and facility facts the navigation is filtered by. One hook, so the sidebar, the
 * overview pages, the phone More sheet, the command palette and the Feature Access dialog all ask
 * the same question the same way.
 */
export function useNavContext(): NavContext {
  const role = useGlobalStore((s) => s.userRole);
  const facilityId = useGlobalStore((s) => s.facilityId);
  const isCorporate = useGlobalStore((s) => !!s.facilitySettings?.isCorporate);
  // WinForms shows the EHR page only when the facility has EHR turned on and the user has the
  // level (frmDietech_Resident_DataGrid_V2: Dietech_Ehr_Level > 0 AndAlso FacilityEhrActive).
  const ehrActive = useGlobalStore((s) => !!s.facilitySettings?.facilityEhrActive);
  const settingsLoaded = useGlobalStore((s) => s.facilitySettings != null);
  const residentPlural = useGlobalStore((s) => s.residentPluralLabel) || 'Residents';
  const ehrTitle = useGlobalStore((s) => {
    const fs = s.facilitySettings as Record<string, unknown> | null;
    const raw = fs?.eHR_Title as string | undefined;
    return displayEhrTitle(raw) || 'EMR';
  });
  const hiddenRoutes = useFeatureVisibilityStore((s) => s.hiddenRoutes);
  const isVisible = useFeatureVisibilityStore((s) => s.isVisible);
  const storeSettings = useGlobalStore((s) => s.facilitySettings);
  const { data: querySettings } = useFacilitySettings(facilityId);
  const facilitySettings = querySettings ?? storeSettings;
  const kdsEnabled = !!facilitySettings?.kDS_Enabled;
  const purchasingLicensed = usePurchasingLicensed();
  // Fails closed: every optional purchasing feature counts as off until the switches load.
  const { features: purchasingFeatures } = usePurchasingFeatures(facilityId, purchasingLicensed);
  const isPsi = useIsPsiStaff();
  const assessmentsOff = useAssessmentsOff();
  // WinForms leaves the Recipes nodes and Items > Linking out under an Assessment Only licence.
  const assessmentOnly = useAssessmentOnlyLicence(facilityId);
  // The route guards' staged rules (Enforce mode only), so the menu never offers a page its guard closes.
  const { enforced } = useOptionalPermissionEnforcement();
  const { settings: mpSettings } = useMealPatternFacilitySettings();
  const globalMealPattern = `Global ${mpSettings.mealPatternShortDescription}`;

  return useMemo<NavContext>(
    () => ({
      // The same rule as usePermission: no role, no access; a missing column is no access.
      roleLoaded: role != null,
      getLevel: (field: string) => (role ? ((role as unknown as Record<string, number | null>)[field] ?? LEVEL.NONE) : LEVEL.NONE),
      isPsi,
      facilityId: facilityId || null,
      purchasingLicensed,
      purchasingFeatures,
      kdsEnabled,
      settingsLoaded,
      ehrActive,
      isCorporate,
      facilitySettings,
      assessmentsOff,
      assessmentOnly,
      isHidden: (key: string) => !isVisible(key),
      labels: { residentPlural, globalMealPattern, ehrTitle },
      stagedRouteAllowed: (securityField: string) => stagedRouteAllows(enforced, role, securityField),
    }),
    // hiddenRoutes is listed although only isVisible is read: isVisible is a stable store function
    // that reads the current set, so without it a Feature Access change would not refilter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [role, isPsi, facilityId, purchasingLicensed, purchasingFeatures, kdsEnabled, settingsLoaded, ehrActive, isCorporate, facilitySettings, assessmentsOff, assessmentOnly, enforced, isVisible, hiddenRoutes, residentPlural, globalMealPattern, ehrTitle],
  );
}

export interface NavSectionsResult {
  /** What this user may see, Feature Access hides applied. Empty until the role loads. */
  sections: NavSectionView[];
  /** False until the role has loaded: show a skeleton, not "you have nothing". */
  ready: boolean;
}

/** The sections, groups and pages the signed-in user may see. Fails closed until the role loads. */
export function useNavSections(): NavSectionsResult {
  const ctx = useNavContext();
  const sections = useMemo(() => buildNavSections(ctx), [ctx]);
  return { sections, ready: ctx.roleLoaded };
}

export interface CurrentNav {
  /** The visible section the current page is in, if any. */
  section: NavSectionView | null;
  /** The visible page the current path is, if any (the longest matching route). */
  page: NavPageView | null;
  /** The id of the section the path belongs to, visible to this user or not. */
  sectionId: string | null;
}

/**
 * Which section and page the current address belongs to. A page listed in two sections (Selective
 * Touch) is in the one the user opened it from, while that section is in the menu, else its home.
 */
export function useCurrentNav(sections: NavSectionView[]): CurrentNav {
  const { pathname } = useLocation();
  const facilityId = useGlobalStore((s) => s.facilityId);
  const entry = useNavEntrySection();
  return useMemo(() => {
    const where = locate(pathname, facilityId);
    if (!where) return { section: null, page: null, sectionId: null };
    const fromEntry = entry ? sections.find((s) => s.id === entry.section.id) ?? null : null;
    const section = fromEntry ?? sections.find((s) => s.id === where.section.id) ?? null;
    const page = where.page && section ? section.pages.find((p) => p.key === where.page!.route) ?? null : null;
    return { section, page, sectionId: fromEntry ? fromEntry.id : where.section.id };
  }, [pathname, facilityId, sections, entry]);
}

/** The command palette's page list (navSearchSources): sections first, then every page by label. */
export function useNavPalette(): { directory: NavSource[]; searchable: NavSource[] } {
  const { sections } = useNavSections();
  const isPsi = useIsPsiStaff();
  return useMemo(() => navSearchSources(sections, isPsi), [sections, isPsi]);
}
